// Deterministically generates a wave's spawn queue from the RNG state.
// Seeding the same RNG + wave + targets reproduces the wave exactly — the
// property the unit tests rely on.

import type { EnemyKind, RngState, SpawnEntry, Vec2 } from '../state/types';
import { floatInRange, intInRange } from '../rng/mulberry32';
import { mirvChance, smartBombChance, waveConfig } from './waveConfig';
import { FIELD_W, GROUND_Y, MIRV_SPLIT_Y, WAVE_SPAWN_WINDOW } from '../constants';

export interface WaveResult {
  readonly queue: readonly SpawnEntry[];
  readonly rng: RngState;
}

/** `targets` are the positions an enemy may aim at (alive cities + bases). */
export function generateWave(
  wave: number,
  rng: RngState,
  targets: readonly Vec2[],
): WaveResult {
  const { count } = waveConfig(wave);
  const fallback: Vec2 = { x: FIELD_W / 2, y: GROUND_Y };
  const smart = smartBombChance(wave);
  const mirv = mirvChance(wave);
  let s = rng;
  const queue: SpawnEntry[] = [];

  for (let i = 0; i < count; i++) {
    const ox = floatInRange(s, 40, FIELD_W - 40);
    s = ox.state;
    const ti = intInRange(s, 0, Math.max(1, targets.length));
    s = ti.state;
    const rel = floatInRange(s, 0, WAVE_SPAWN_WINDOW);
    s = rel.state;
    const roll = floatInRange(s, 0, 1);
    s = roll.state;

    let kind: EnemyKind = 'icbm';
    let splitAt: number | null = null;
    if (roll.value < smart) {
      kind = 'smartBomb';
    } else if (roll.value < smart + mirv) {
      splitAt = MIRV_SPLIT_Y; // a MIRV ICBM
    }

    const target = targets[ti.value] ?? fallback;
    queue.push({
      releaseAt: rel.value,
      kind,
      origin: { x: ox.value, y: 0 },
      target,
      splitAt,
    });
  }

  queue.sort((a, b) => a.releaseAt - b.releaseAt);
  return { queue, rng: s };
}
